import { FormLabel, Grid, Typography } from '@mui/material'
import React from 'react'
import { ResultsP } from '../../../components/styled/uiComponents'

interface Props {
  formState: any
  user?: any
  metaData?: any
}

const safeParse = (value: any) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }
  try {
    return JSON.parse(value)
  } catch (_err) {
    return null
  }
}

const Summary: React.FC<Props> = ({ formState, user }) => {
  const postikieli = safeParse(formState?.address?.els_postikieli)
  const country = safeParse(formState?.address?.country)
  let markkinointi = safeParse(formState?.address?.markkinoinninesto) as any
  if (!markkinointi && user?.markkinoinninesto) {
    markkinointi = user.markkinoinninesto
  }
  const oppilaitos = safeParse(formState?.cursus?.els_oppilaitos)
  const lukukausi = safeParse(formState?.cursus?.els_lukukausi)
  const aloitusvuosi = formState?.cursus?.els_aloitusvuosi

  // Fallbacks from sidosryhmä if form values are missing
  let srOppilaitosName: string | undefined
  let srLukukausiValue: string | undefined
  let srAloitusvuosi: string | undefined

  if (user?.sidosryhmat && Array.isArray(user.sidosryhmat)) {
    const now = new Date()
    const sr = user.sidosryhmat.find((s: any) => {
      const isStudent = s['paataso.els_paataso'] === 'Lääketieteen opiskelijat'
      const end = s['els_paattymispaiva'] ? new Date(s['els_paattymispaiva']) : null
      return isStudent && (!end || end > now)
    })
    if (sr) {
      // Tarkenne format: "Lääketieteen opiskelija - <Oppilaitos>"
      const tarkenne: string | undefined = sr['tarkenne.els_sidosryhmantarkenne']
      if (tarkenne) {
        const parts = tarkenne.split(' - ')
        srOppilaitosName = parts[parts.length - 1]?.trim()
      }
      const alkupaiva: string | undefined = sr['els_alkupaiva']
      if (alkupaiva) {
        const d = new Date(alkupaiva)
        const year = d.getFullYear()
        srAloitusvuosi = `${year}`
        const juneFirst = new Date(year, 5, 1)
        srLukukausiValue = d < juneFirst ? 'Kevät' : 'Syksy'
      }
    }
  }
  if (
    typeof formState.cursus.els_oppilaitos !== 'object' ||
    formState.cursus.els_oppilaitos === null
  ) {
    formState.cursus.els_oppilaitos = {}
    if (oppilaitos) {
      formState.cursus.els_oppilaitos = oppilaitos
    }
  }

  if (!formState.cursus.els_oppilaitos.els_koodiid) {
    formState.cursus.els_oppilaitos.els_koodiid = 'd42381f6-f72b-ec11-b6e5-000d3a25c645'
  }
  if (!formState.cursus.els_oppilaitos.els_koodinnimi) {
    formState.cursus.els_oppilaitos.els_koodinnimi = srOppilaitosName
  }

  if (
    typeof formState.cursus.els_lukukausi !== 'object' ||
    formState.cursus.els_lukukausi === null
  ) {
    formState.cursus.els_lukukausi = {}
    if (lukukausi) {
      formState.cursus.els_lukukausi = lukukausi
    }
  }

  if (!formState.cursus.els_lukukausi.attributevalue) {
    formState.cursus.els_lukukausi.attributevalue = 1
  }
  if (!formState.cursus.els_lukukausi.value) {
    formState.cursus.els_lukukausi.value = srLukukausiValue
  }

  if (!formState.cursus.els_aloitusvuosi) {
    formState.cursus.els_aloitusvuosi = srAloitusvuosi
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant='body1'>Olet lähettämässä lomaketta seuraavilla tiedoilla:</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant='h5' color='primary'>
          Henkilötiedot
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Sukunimi</FormLabel>
        <ResultsP>{formState.lastname}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Etunimet</FormLabel>
        <ResultsP>{formState.firstname}</ResultsP>
      </Grid>
      {formState.nickname && (
        <Grid item xs={12}>
          <FormLabel>Kutsumanimi</FormLabel>
          <ResultsP>{formState.nickname}</ResultsP>
        </Grid>
      )}
      <Grid item xs={12}>
        <FormLabel>Henkilötunnus</FormLabel>
        <ResultsP>{formState.els_hetu}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <div style={{ borderTop: '1px dotted black' }}>
          <Typography variant='h5' color='primary' sx={{ paddingTop: '3vh' }}>
            Yhteystiedot
          </Typography>
        </div>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Postiosoite</FormLabel>
        <ResultsP>{formState.address.line1}</ResultsP>
      </Grid>
      {formState.address.line2 && (
        <Grid item xs={12}>
          <FormLabel>Postiosoitteen rivi 2</FormLabel>
          <ResultsP>{formState.address.line2}</ResultsP>
        </Grid>
      )}
      {formState.address.line3 && (
        <Grid item xs={12}>
          <FormLabel>Postiosoitteen rivi 3</FormLabel>
          <ResultsP>{formState.address.line3}</ResultsP>
        </Grid>
      )}
      <Grid item xs={12}>
        <FormLabel>Postinumero</FormLabel>
        <ResultsP>{formState.address.postalcode}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Postitoimipaikka</FormLabel>
        <ResultsP>{formState.address.city}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Postituskieli</FormLabel>
        <ResultsP>{postikieli?.value || '-'}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Osoitteen luovutus</FormLabel>
        <ResultsP>{markkinointi?.value || '-'}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Maa</FormLabel>
        <ResultsP>{country?.els_koodinnimi || '-'}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Matkapuhelinnumero</FormLabel>
        <ResultsP>{formState.mobilephone}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <FormLabel>Sähköpostiosoite</FormLabel>
        <ResultsP>{formState.emailaddress1}</ResultsP>
      </Grid>
      <Grid item xs={12}>
        <div style={{ borderTop: '1px dotted black' }}>
          <Typography variant='h5' color='primary' sx={{ paddingTop: '3vh' }}>
            Opiskelutiedot
          </Typography>
        </div>
      </Grid>
      {(oppilaitos ||
        srOppilaitosName ||
        aloitusvuosi ||
        srAloitusvuosi ||
        lukukausi ||
        srLukukausiValue) && (
        <>
          <Grid item xs={12}>
            <FormLabel>Oppilaitos</FormLabel>
            <ResultsP>
              {oppilaitos?.els_koodinnimi ||
                formState.cursus.els_oppilaitos.els_koodinnimi ||
                srOppilaitosName ||
                '-'}
            </ResultsP>
          </Grid>
          <Grid item xs={12}>
            <FormLabel>Aloitusvuosi</FormLabel>
            <ResultsP>
              {aloitusvuosi || formState.cursus.els_aloitusvuosi || srAloitusvuosi || '-'}
            </ResultsP>
          </Grid>
          <Grid item xs={12}>
            <FormLabel>Lukukausi</FormLabel>
            <ResultsP>
              {lukukausi?.value || formState.cursus.els_lukukausi.value || srLukukausiValue || '-'}
            </ResultsP>
          </Grid>
        </>
      )}
    </Grid>
  )
}
export default Summary
